/**
 * Ventana de números de página
 * -----------------------------------------------------------------------------
 * La comparten la vista de datos y la lista de notificaciones. Estaba dentro de
 * `data-view.js`, y al necesitarla la segunda lo correcto era sacarla: dos
 * copias de esto acaban divergiendo en los bordes, que es justo donde cuesta
 * darse cuenta.
 *
 * Un listado de 4.000 registros de siete en siete son 600 páginas; pintarlas
 * todas llena la pantalla de botones y deja el contenido fuera de la vista. Se
 * muestra una ventana alrededor de la actual y **siempre la primera y la
 * última**, con un hueco en medio: sin las de los extremos, llegar al final
 * desde la página 1 costaría ciento veinte clics.
 */

/**
 * Marca de hueco entre bloques de páginas. Un símbolo y no la cadena '…' para
 * que no pueda confundirse con un número que venga como texto.
 */
export const GAP = Symbol('gap');

/** Botones numerados alrededor del actual, sin contar primera y última. */
export const MAX_PAGES = 5;

/**
 * @param {number} page Página actual, empezando en 1.
 * @param {number} pages Total de páginas.
 * @param {number} [max=MAX_PAGES] Cuántos números mostrar alrededor.
 * @returns {Array<number|symbol>} Números, con `GAP` donde va el hueco.
 */
export function pageNumbers(page, pages, max = MAX_PAGES) {
  // Con pocas caben todas, y meter puntos suspensivos para saltarse una sola
  // página sería más ruido que ayuda.
  if (pages <= max + 2) {
    return Array.from({ length: pages }, (unused, i) => i + 1);
  }

  const half = Math.floor(max / 2);
  let start = Math.max(2, page - half);
  let end = Math.min(pages - 1, page + half);

  // Cerca de un extremo la ventana se corta por ese lado; se estira por el otro
  // para que el número de botones no baile al pasar de página.
  if (page - half < 2) end = Math.min(pages - 1, max);
  if (page + half > pages - 1) start = Math.max(2, pages - max + 1);

  const numbers = [1];
  if (start > 2) numbers.push(GAP);
  for (let n = start; n <= end; n += 1) numbers.push(n);
  if (end < pages - 1) numbers.push(GAP);
  numbers.push(pages);

  return numbers;
}

/**
 * Pinta los controles de página: anterior, los números y siguiente.
 *
 * Devuelve el nodo o `null` si solo hay una página: unos controles que no
 * llevan a ninguna parte son ruido.
 *
 * @param {Object} options `{ page, pages, maxPages, onPage, previousLabel, nextLabel }`
 * @returns {HTMLElement|null}
 */
export function renderPagerControls({ page, pages, maxPages, onPage, previousLabel, nextLabel }) {
  if (pages <= 1) return null;

  const controls = document.createElement('div');
  controls.className = 'data-pager-controls';

  const boton = (label, target, disabled, aria) => {
    const node = document.createElement('button');
    node.type = 'button';
    node.className = 'data-pager-nav';
    node.innerHTML = label;
    node.disabled = disabled;
    if (aria) node.setAttribute('aria-label', aria);
    if (!disabled) node.addEventListener('click', () => onPage(target));
    return node;
  };

  controls.appendChild(
    boton('<i class="fas fa-chevron-left"></i>', page - 1, page === 1, previousLabel)
  );

  pageNumbers(page, pages, maxPages).forEach((n) => {
    if (n === GAP) {
      const hueco = document.createElement('span');
      hueco.className = 'data-pager-gap';
      hueco.setAttribute('aria-hidden', 'true');
      hueco.textContent = '…';
      controls.appendChild(hueco);
      return;
    }

    const node = document.createElement('button');
    node.type = 'button';
    node.className = `data-pager-page${n === page ? ' active' : ''}`;
    node.textContent = String(n);
    node.setAttribute('aria-current', n === page ? 'page' : 'false');
    node.addEventListener('click', () => onPage(n));
    controls.appendChild(node);
  });

  controls.appendChild(
    boton('<i class="fas fa-chevron-right"></i>', page + 1, page === pages, nextLabel)
  );

  return controls;
}
